import { IconCheck } from '@leland-dev/leland-ui-library';
import { unionBy } from 'lodash-es';
import React, { useCallback, useMemo, useState } from 'react';

import { type GoalName } from '../../__generated-gql-types__/globalTypes';
import { useDebounce } from '../../utils/hooks/useDebounce';
import {
  MultiTypeahead,
  type MultiTypeaheadProps,
  SingleTypeahead,
  type SingleTypeaheadProps,
} from '../inputs/Typeahead';
import { type SelectOptionType } from '../inputs/selects';

import {
  type CategoryTypeaheadCategoryFragment,
  useCategoryTypeaheadQuery,
} from './__generated-gql-types__/CategoryTypeahead.generated';

export type CategoryTypeaheadCategory = CategoryTypeaheadCategoryFragment;

export interface AllOption {
  id: 'all';
  slug: 'all';
  name: string;
}

const isCategoryTypeaheadCategoryEqual = (
  c1: CategoryTypeaheadCategory,
  c2: CategoryTypeaheadCategory,
) => c1.id === c2.id;

type SingleCategoryTypeaheadProps = Omit<
  SingleTypeaheadProps<CategoryTypeaheadCategory>,
  'options' | 'onInputChange' | 'defaultOption'
> & {
  goal?: Nullable<GoalName>;
  featuredOnly?: boolean;
  popularEventsOnly?: boolean;
  defaultOption?: CategoryTypeaheadCategory | AllOption;
  initialOptions?: Array<CategoryTypeaheadCategory | AllOption>;
};

export const SingleCategoryTypeahead: React.FC<
  SingleCategoryTypeaheadProps
> = ({
  goal,
  featuredOnly,
  popularEventsOnly,
  initialOptions,
  value,
  placeholder = 'Search categories...',
  defaultOption,
  ...props
}) => {
  const queryProps = useCategoryTypeaheadQueryData({
    goal,
    value,
    featuredOnly,
    popularEventsOnly,
    initialOptions,
  });

  return (
    <SingleTypeahead<CategoryTypeaheadCategory | AllOption>
      {...props}
      value={value}
      isValueEqual={isCategoryTypeaheadCategoryEqual}
      placeholder={placeholder}
      renderOption={(option, isSelected) => (
        <div className="flex w-full items-center justify-between gap-2 font-medium">
          {option.name}
          {isSelected ? <IconCheck className="size-4" /> : null}
        </div>
      )}
      defaultOption={defaultOption}
      {...queryProps}
    />
  );
};

type MultiCategoryTypeaheadProps = Omit<
  MultiTypeaheadProps<CategoryTypeaheadCategory>,
  'options' | 'onInputChange'
> & {
  goal?: Nullable<GoalName>;
  featuredOnly?: boolean;
};

export const MultiCategoryTypeahead: React.FC<MultiCategoryTypeaheadProps> = ({
  goal,
  featuredOnly,
  values,
  placeholder = 'Search categories...',
  ...props
}) => {
  const queryProps = useCategoryTypeaheadQueryData({
    goal,
    values,
    featuredOnly,
  });

  return (
    <MultiTypeahead
      {...props}
      values={values}
      isValueEqual={isCategoryTypeaheadCategoryEqual}
      placeholder={placeholder}
      {...queryProps}
    />
  );
};

const categoryToSelectOptionType = (
  category: CategoryTypeaheadCategory,
): SelectOptionType<CategoryTypeaheadCategory> => ({
  value: category,
  label: category.name,
});

const useCategoryTypeaheadQueryData = ({
  goal,
  featuredOnly,
  popularEventsOnly,
  initialOptions,
  value,
  values,
}: {
  goal?: Nullable<GoalName>;
  featuredOnly?: boolean;
  popularEventsOnly?: boolean;
  initialOptions?: Array<CategoryTypeaheadCategory | AllOption>;
  value?: Nullable<CategoryTypeaheadCategory>;
  values?: CategoryTypeaheadCategory[];
}) => {
  const [rawQuery, setRawQuery] = useState<string>('');
  const query = useDebounce(rawQuery, 200);
  const onInputChange = useCallback((input: string) => setRawQuery(input), []);

  const { data, loading } = useCategoryTypeaheadQuery({
    variables: { goal, query, featuredOnly, popularEventsOnly },
  });

  const options: Array<SelectOptionType<CategoryTypeaheadCategory>> =
    useMemo(() => {
      const categories = unionBy(
        data?.categoryTypeahead ?? [],
        value ? [value] : values,
        'id',
      );
      return [
        ...(initialOptions?.map(categoryToSelectOptionType) ?? []),
        // Sort the categories so that the selected category is first
        ...categories.map(categoryToSelectOptionType).sort((a, b) => {
          if (value && a.value.id === value.id) return -1;
          if (value && b.value.id === value.id) return 1;
          if (values?.some((v) => v.id === a.value.id)) return -1;
          if (values?.some((v) => v.id === b.value.id)) return 1;
          return 0;
        }),
      ];
    }, [data?.categoryTypeahead, initialOptions, value, values]);

  return { onInputChange, options, loading };
};
